import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useDistrict } from "@/lib/district-context";

// Reintentos del lookup de familia cuando ministry_status='transferred' ya
// garantiza que debería existir una familia flotante, pero la consulta no la
// trajo en el primer intento (red, timing, lo que sea). Backoff simple:
// 600ms, 1200ms, 1800ms — ~3.6s tope antes de rendirse.
const FAMILY_LOOKUP_MAX_RETRIES = 3;
const FAMILY_LOOKUP_RETRY_DELAY_MS = 600;

/**
 * Detects if the authenticated pastor is in a "floating" state requiring
 * pastoral-home onboarding. Two modes:
 *
 *   mode='transferred' — transferido entre distritos, familia huérfana sin iglesia.
 *     Señales: ministry_status==='transferred' (gana siempre, aun sin person_id)
 *              o family.church_id===null && family.district_id!==null (lookup vía person_id).
 *     Acción: completeTransferOnboarding (asigna la familia existente a una iglesia).
 *
 *   mode='setup_new' — pastor con distrito pero SIN hogar pastoral (person_id null),
 *     no transferido. Cubre tanto al pastor recién bootstrapeado como a los existentes
 *     que nunca configuraron su hogar. Acción: crear hogar + anclar (createPastoralIdentity).
 *
 * 'transferred' tiene prioridad si ambas aplicaran. Coordinador siempre excluido
 * (pasado el guard, el usuario ES pastor por la definición del contexto, isPastor).
 *
 * Admin: excluido salvo que se haya soltado de su propio distrito
 * (release_pastor_district) y YA tenga uno nuevo (bootstrap_district) — ese es el
 * único momento en que un admin puede estar en modo 'transferred' con una familia
 * flotante, igual que cualquier pastor transferido. Mientras el admin no tenga
 * distrito (district_id null), el wizard NO debe activarse — de eso se encarga el
 * redirect a /create-district en RequireDistrict (App.js); dejarlo pasar aquí
 * produciría un wizard sin distrito contra el cual listar iglesias.
 *
 * COHERENCIA mode/family (con person_id presente): mode SOLO se fija en
 * 'transferred' junto con una family real ya resuelta — nunca por separado.
 * Si ministry_status='transferred' pero el lookup no trae familia en un
 * intento, se reintenta (ver constantes arriba) en vez de resolver a
 * mode='transferred' con family vacío — ese estado inconsistente es lo que
 * hacía que FloatingWizard cayera en needsHomeSetup (!family) y ofreciera
 * "crear hogar" en vez de "asignar tu familia existente". Si los reintentos
 * se agotan sin encontrar family, se resuelve a no-floating (nada visible)
 * en vez de al estado roto — preferible a mostrar el flujo equivocado.
 *
 * Returns:
 *   isFloating — boolean
 *   mode       — 'transferred' | 'setup_new' | null
 *   family     — null en setup_new / cuando la persona no está vinculada; objeto si se halló
 *   loading    — true mientras el lookup async (y sus reintentos) siguen en curso
 */
export function useFloatingState() {
  const { userProfile, district, loading: ctxLoading, isAdmin, isCoordinator } = useDistrict();

  const [isFloating,   setIsFloating]   = useState(false);
  const [mode,         setMode]         = useState(null);
  const [family,       setFamily]       = useState(null);
  const [loadingFloat, setLoadingFloat] = useState(false);

  useEffect(() => {
    if (ctxLoading || !userProfile || isCoordinator) {
      setIsFloating(false);
      setMode(null);
      setFamily(null);
      return;
    }

    if (isAdmin && !userProfile.district_id) {
      // Admin recién soltado, aún sin distrito nuevo: RequireDistrict ya lo manda
      // a /create-district. El wizard no aplica hasta que tenga distrito.
      setIsFloating(false);
      setMode(null);
      setFamily(null);
      return;
    }

    // Transferred wins. Works even when person_id is not yet linked.
    const floatingByStatus = userProfile?.ministry_status === 'transferred';
    const personId = userProfile.person_id;

    // NEW: pastor con distrito pero sin hogar pastoral (no transferido). person_id null por
    // definición -> no requiere lookup, se decide solo con campos del perfil (estable offline).
    const setupNew = !floatingByStatus && !!userProfile.district_id && !personId;

    if (!personId) {
      // Sin persona vinculada -> no hay familia que buscar.
      if (floatingByStatus) {
        setIsFloating(true); setMode('transferred'); setFamily(null);
      } else if (setupNew) {
        setIsFloating(true); setMode('setup_new'); setFamily(null);
      } else {
        setIsFloating(false); setMode(null); setFamily(null);
      }
      return;
    }

    // person_id presente — lookup de la familia (camino transferred existente, intacto).
    let cancelled = false;
    let retryTimer = null;
    setLoadingFloat(true);

    const resolveFloating = (fam) => {
      if (cancelled) return;
      setIsFloating(true);
      setMode('transferred');
      setFamily(fam);
      setLoadingFloat(false);
    };

    const resolveNotFloating = () => {
      if (cancelled) return;
      setIsFloating(false);
      setMode(null);
      setFamily(null);
      setLoadingFloat(false);
    };

    const attemptLookup = (attempt) => {
      supabase
        .from("people")
        .select("family_id, families!people_family_id_fkey!inner(id, name, church_id, district_id)")
        .eq("id", personId)
        .maybeSingle()
        .then(({ data }) => {
          if (cancelled) return;
          const fam = data?.families;
          const floatingByFamily = fam?.church_id === null && fam?.district_id !== null;

          // Solo se resuelve 'transferred' cuando REALMENTE hay una family en
          // mano — nunca mode='transferred' con family vacío.
          if (fam && (floatingByFamily || floatingByStatus)) {
            resolveFloating(fam);
            return;
          }

          if (floatingByStatus && attempt < FAMILY_LOOKUP_MAX_RETRIES) {
            // ministry_status dice que debería haber familia pero esta vuelta
            // no la trajo — reintenta en vez de resolver a un estado roto.
            retryTimer = setTimeout(
              () => attemptLookup(attempt + 1),
              FAMILY_LOOKUP_RETRY_DELAY_MS * (attempt + 1)
            );
            return;
          }

          resolveNotFloating();
        })
        .catch(() => {
          if (cancelled) return;
          if (floatingByStatus && attempt < FAMILY_LOOKUP_MAX_RETRIES) {
            retryTimer = setTimeout(
              () => attemptLookup(attempt + 1),
              FAMILY_LOOKUP_RETRY_DELAY_MS * (attempt + 1)
            );
            return;
          }
          resolveNotFloating();
        });
    };

    attemptLookup(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.id, userProfile?.person_id, userProfile?.ministry_status, userProfile?.district_id, ctxLoading, isAdmin, isCoordinator]);

  return {
    isFloating,
    mode,
    family,
    loading: loadingFloat,
    district,
    userProfile,
  };
}
